import {
	buildPlatformLogoutUrl,
	ConnectingScreen as FatalConnectScreen,
	DesktopShell,
	extractRuntimeKind,
	getActiveKernelToken,
	setAppEnvClient,
	setAppRenderer,
	useDesktopStore,
	useRetryStore,
} from '@khal-os/desktop-shell';
import { BUILTIN_COMPONENTS, BUILTIN_LABELS } from './builtin-apps';
import { appEnvClient } from './app-env-client';
import type { AppComponentRenderer } from '@khal-os/desktop-shell';
import React, { useCallback, useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import ReactDOM from 'react-dom/client';
import { AuthAcceptScreen } from './components/AuthAcceptScreen';
import { AuthChooseScreen } from './components/AuthChooseScreen';
import { ConnectionErrorScreen } from './components/ConnectionErrorScreen';
import { KernelUrlPromptScreen } from './components/KernelUrlPromptScreen';
import { DynamicAppBundle } from './components/DynamicAppBundle';
import { KhalAppFrame } from './components/KhalAppFrame';
// PwaBadge + DownloadAffordance removed from chrome — PWA should look identical
// to Tauri. Native-download links belong in Settings → Updates, not as a
// floating taskbar element. Remove silently; re-add in Settings pack later.
import { getAdapter } from './lib/adapters';
import { resolveDefaultKernelForPwaOrigin } from './lib/default-kernel';
import { getEnterpriseConfig, showLauncher, switchAccount } from './lib/tauri';
// pack-registry supplies the static `packs.json` manifest (id/name/icon).
// It is now a FIRST-PAINT fallback only — the authoritative source for the
// launcher is the kernel registry, fetched via NATS `os.apps.list` into
// `useDesktopStore.installedAppRowsBySlug` and refreshed on
// `os.apps.registry.changed`. Static packs let the shell render before NATS
// connects (and offline). Without that fallback, the first paint flashes an
// empty desktop until the request resolves.
import { getRegisteredPacks } from './pack-registry';
import { TauriKhalAuthProvider } from './providers/TauriKhalAuthProvider';
// ThemeProvider from @khal-os/ui wraps next-themes — required by every UI
// component that calls useTheme() from next-themes (e.g. ThemeSwitcher in
// pack-settings). The previous custom Context-based ThemeProvider only
// updated its own consumers, so ThemeSwitcher saw `theme: undefined` and
// rendered every radio unchecked. The kit-provided variant also wires
// reduce-motion, glass, and gpu-terminals syncs out of the box.
import { ThemeProvider } from '@khal-os/ui';
import { initSentry, Sentry } from './sentry';
import { connectWithRetry, manualRetry } from './ws-retry';
import './index.css';

// Initialize Sentry BEFORE React render
initSentry();

// Track the enterprise server URL for error display
let _enterpriseServerUrl: string | undefined;

// In Tauri mode, start WS connection with retry (fire-and-forget, banner shows progress)
getEnterpriseConfig().then((config) => {
	if (config?.wsUrl) {
		_enterpriseServerUrl = config.serverUrl;
		connectWithRetry().catch(console.error);
	}
});

// Background update check on launch (Tauri only, fire-and-forget)
if ((window as any).__TAURI__) {
	import('@tauri-apps/plugin-updater')
		.then(({ check }) => {
			check()
				.then((update) => {
					if (update) {
						console.log(`[updater] Update available: ${update.version}`);
						import('@khal-os/desktop-shell').then(({ useUpdateStore }) => {
							useUpdateStore.getState().setAvailable(update.version);
						});
					} else {
						console.log('[updater] App is up to date');
					}
				})
				.catch((err) => console.warn('[updater] Check failed:', err));
		})
		.catch((err) => console.warn('[updater] Plugin not available:', err));

	// Listen for native macOS menu events
	const tauri = (window as any).__TAURI__;
	if (tauri?.event?.listen) {
		tauri.event.listen('menu-check-updates', () => {
			import('@tauri-apps/plugin-updater')
				.then(({ check }) => {
					check()
						.then((update) => {
							if (update) {
								import('@khal-os/desktop-shell').then(({ useUpdateStore }) => {
									useUpdateStore.getState().setAvailable(update.version);
								});
							}
						})
						.catch(console.error);
				})
				.catch(console.error);
		});
		tauri.event.listen('menu-preferences', () => {
			import('@khal-os/desktop-shell').then(({ useWindowStore }) => {
				useWindowStore.getState().openWindow({ title: 'Settings', appId: 'settings' });
			});
		});
	}
}

// Capture unhandled promise rejections
window.addEventListener('unhandledrejection', (e) => {
	Sentry.captureException(e.reason);
});

/** Listen for the tray "Switch Account" menu event and invoke the Rust command. */
function useSwitchAccountListener() {
	useEffect(() => {
		const tauri = (window as any).__TAURI__;
		if (!tauri?.event?.listen) return;

		let unlisten: (() => void) | undefined;
		tauri.event.listen('switch-account', () => {
			switchAccount().catch(console.error);
		}).then((fn: () => void) => {
			unlisten = fn;
		});

		return () => unlisten?.();
	}, []);
}

/**
 * K dot-matrix pattern — 13 rows x 14 columns.
 * 1 = lit (pulse animation), 0 = off (transparent).
 * Matches the launcher's Room 4 animation exactly.
 */
const K_MATRIX = [
	[1,1,0,0,0,0,0,0,0,0,1,1,1,0],
	[1,1,0,0,0,0,0,0,0,1,1,1,0,0],
	[1,1,0,0,0,0,0,0,1,1,1,0,0,0],
	[1,1,0,0,0,0,0,0,1,1,0,0,0,0],
	[1,1,0,0,0,0,0,1,1,1,0,0,0,0],
	[1,1,0,0,0,0,1,1,1,0,0,0,0,0],
	[1,1,1,1,1,1,1,1,0,0,0,0,0,0],
	[1,1,1,1,1,1,1,1,1,0,0,0,0,0],
	[1,1,0,0,0,0,0,1,1,1,0,0,0,0],
	[1,1,0,0,0,0,0,0,1,1,1,0,0,0],
	[1,1,0,0,0,0,0,0,0,1,1,1,0,0],
	[1,1,0,0,0,0,0,0,0,0,1,1,1,0],
	[1,1,0,0,0,0,0,0,0,0,1,1,1,1],
];

/**
 * Connecting screen — shown while the WS/NATS connection is being established.
 * Replaces the broken "empty shell" that used to render during the retry window.
 * Uses the same K dot-matrix animation from the launcher for visual continuity.
 */
function ConnectingScreen() {
	const retryCount = useRetryStore((s) => s.retryCount);
	const retryIn = useRetryStore((s) => s.retryIn);
	const status = useRetryStore((s) => s.status);

	let statusText = 'Connecting…';
	if (status === 'retry_waiting' && retryIn > 0) {
		statusText = `Reconnecting in ${retryIn}s… (attempt ${retryCount + 1})`;
	} else if (status === 'connecting' && retryCount > 0) {
		statusText = `Retrying… (attempt ${retryCount + 1})`;
	}

	const isTauri = typeof window !== 'undefined' && !!(window as unknown as { __TAURI__?: unknown }).__TAURI__;
	// Show the sign-out escape after a few failed attempts, or immediately in
	// retry_waiting state. Users deserve a visible exit when connect keeps
	// failing, not just a silent spinner. Hidden in Tauri (tray handles it).
	const showSignOut = !isTauri && (retryCount >= 2 || status === 'retry_waiting' || status === 'retry_failed');

	return (
		<div className="connecting-screen">
			<div className="connecting-status">{statusText}</div>
			<div className="connecting-dot-matrix">
				{K_MATRIX.flat().map((lit, i) => (
					<div
						key={i}
						className={lit ? 'connecting-dot pulse' : 'connecting-dot off'}
						style={lit ? { animationDelay: `${Math.floor(i / 14) * 0.07}s` } : undefined}
					/>
				))}
			</div>
			{showSignOut && (
				<a
					href="/auth/reset"
					style={{
						marginTop: '2rem',
						color: '#888',
						fontSize: '0.75rem',
						textDecoration: 'underline',
					}}
				>
					Sign out and try a different workspace
				</a>
			)}
		</div>
	);
}

/**
 * /auth/reset — unconditional session escape.
 *
 * Purges every stored enterprise config and redirects to the platform
 * sign-in. Used by the in-shell "Sign out" links and as a direct URL
 * anyone can bookmark when they're stuck. No confirmation — hitting
 * this URL means the user wants to leave, full stop.
 */
function AuthResetScreen() {
	useEffect(() => {
		const adapter = getAdapter();
		(async () => {
			// Snapshot the kernel-session JWT BEFORE wiping storage so
			// platform can extract the `sid` claim and revoke the matching
			// WorkOS session. Skip the snapshot on SSR (no `window`).
			const token = typeof window !== 'undefined' ? getActiveKernelToken() : null;
			try {
				await adapter.clearEnterpriseConfig?.();
			} catch (err) {
				console.error('[auth-reset] clearEnterpriseConfig failed', err);
			}
			const platformUrl = import.meta.env.VITE_PLATFORM_API_URL ?? 'https://platform.khal.ai';
			const pwaOrigin = window.location.origin;
			// Route through platform /v1/auth/logout instead of straight into
			// /auth/kernel-sign-in so the WorkOS AuthKit session actually
			// terminates — otherwise hitting /auth/reset only clears PWA
			// storage while the WorkOS cookie silently re-authenticates on
			// the next kernel click. Platform 302s back to `pwaOrigin` once
			// WorkOS finishes, where the auth gate lands the user on the
			// kernel picker.
			const logoutUrl = buildPlatformLogoutUrl({
				platformUrl,
				pwaOrigin,
				token,
			});
			window.location.replace(logoutUrl);
		})();
	}, []);

	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: '#0a0a0a',
				color: '#888',
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				fontSize: '0.85rem',
			}}
		>
			Signing out…
		</div>
	);
}

function App() {
	useSwitchAccountListener();

	// SSO landing pages — each runs its own fetch/redirect and short-circuits
	// the rest of the app tree. Matching on pathname keeps this a pure Vite
	// SPA (no react-router) — vercel.json rewrites every path to /index.html,
	// so we pick the handler here.
	if (typeof window !== 'undefined' && window.location.pathname === '/auth/choose') {
		return (
			<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
				<AuthChooseScreen />
			</ThemeProvider>
		);
	}
	if (typeof window !== 'undefined' && window.location.pathname === '/auth/accept') {
		return (
			<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
				<AuthAcceptScreen />
			</ThemeProvider>
		);
	}
	if (typeof window !== 'undefined' && window.location.pathname === '/auth/reset') {
		return (
			<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
				<AuthResetScreen />
			</ThemeProvider>
		);
	}
	if (typeof window !== 'undefined' && window.location.pathname === '/auth/start') {
		return (
			<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
				<KernelUrlPromptScreen />
			</ThemeProvider>
		);
	}

	const retryStatus = useRetryStore((s) => s.status);
	const lastError = useRetryStore((s) => s.lastError);
	// null = unknown (still resolving IPC). MUST NOT default to false — that
	// would skip the enterprise gate on the first render and flash DesktopShell.
	const [isEnterprise, setIsEnterprise] = useState<boolean | null>(null);
	const isTauri = typeof window !== 'undefined' && !!(window as unknown as { __TAURI__?: unknown }).__TAURI__;

	// Check if we're in enterprise mode on mount
	useEffect(() => {
		getEnterpriseConfig().then((config) => {
			setIsEnterprise(!!config?.wsUrl);
		});
	}, []);

	// PWA safety net: if the kernel closes the WS with a fatal auth error OR
	// the retry loop has exhausted all attempts, the stored JWT is effectively
	// dead (expired / rejected / wrong-kernel) and reloading would just replay
	// it. Purge the enterprise config and bounce to the platform sign-in so
	// the user lands in the picker flow with a fresh token instead of looping
	// on a dead desktop shell. Tauri retains its own switch-account UX —
	// skip this path there.
	useEffect(() => {
		if (isTauri) return;

		const isFatalAuth =
			retryStatus === 'fatal' &&
			lastError &&
			(lastError.kind === 'token-expired' || lastError.kind === 'unauthenticated');
		const isRetryExhausted = retryStatus === 'retry_failed';

		if (!isFatalAuth && !isRetryExhausted) return;

		const adapter = getAdapter();
		const resetAndSignIn = async () => {
			await adapter.clearEnterpriseConfig?.();
			const platformUrl = import.meta.env.VITE_PLATFORM_API_URL ?? 'https://platform.khal.ai';
			// No hardcoded customer URL — prefer the live kernel we were just
			// connected to, then the build-time env override, then nothing.
			// Without a known kernel we drop the user on the URL-prompt screen
			// (`/auth/start`) so they can enter their workspace explicitly.
			const candidate = _enterpriseServerUrl ?? resolveDefaultKernelForPwaOrigin({
				pwaOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
				buildDefaultKernel: import.meta.env.VITE_DEFAULT_KERNEL,
			});
			const pwaOrigin = window.location.origin;
			if (!candidate) {
				window.location.replace('/auth/start');
				return;
			}
			const signInUrl = `${platformUrl}/auth/kernel-sign-in?kernel=${encodeURIComponent(candidate)}&pwaOrigin=${encodeURIComponent(pwaOrigin)}`;
			window.location.replace(signInUrl);
		};
		resetAndSignIn().catch((err) => {
			console.error('[auth-reset] failed to clear enterprise config', err);
		});
	}, [retryStatus, lastError, isTauri]);

	const handleBackToLauncher = useCallback(() => {
		showLauncher().catch(console.error);
	}, []);

	const handleRetry = useCallback(() => {
		manualRetry().catch(console.error);
	}, []);

	// Still resolving enterprise config — show connecting screen, never flash DesktopShell.
	if (isEnterprise === null) {
		return (
			<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
				<ConnectingScreen />
			</ThemeProvider>
		);
	}

	// PWA with no enterprise config → user hasn't signed in yet. If a build-time
	// kernel hint exists, bounce to platform's /auth/kernel-sign-in bridge.
	// Otherwise show the URL-prompt screen so the user can name their workspace
	// explicitly — never silently default to a customer-specific kernel.
	// Tauri mode handles this via the launcher window; on the web there's no
	// launcher, so we redirect.
	if (!isEnterprise && !isTauri) {
		const pwaOrigin = typeof window !== 'undefined' ? window.location.origin : '';
		const defaultKernel = resolveDefaultKernelForPwaOrigin({
			pwaOrigin,
			buildDefaultKernel: import.meta.env.VITE_DEFAULT_KERNEL,
		});
		if (!defaultKernel) {
			return (
				<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
					<KernelUrlPromptScreen />
				</ThemeProvider>
			);
		}
		const platformUrl = import.meta.env.VITE_PLATFORM_API_URL ?? 'https://platform.khal.ai';
		// If platform logout bounced us back with `?forceReauth=1` (second-layer
		// defense when WorkOS session-id was unavailable for server-side
		// termination), thread it through so kernel-sign-in adds `prompt=login`
		// to the WorkOS URL. Without this, silent re-auth is still possible.
		const forceReauth =
			typeof window !== 'undefined' &&
			new URLSearchParams(window.location.search).get('forceReauth') === '1';
		const signInUrl = `${platformUrl}/auth/kernel-sign-in?kernel=${encodeURIComponent(defaultKernel)}&pwaOrigin=${encodeURIComponent(pwaOrigin)}${forceReauth ? '&forceReauth=1' : ''}`;
		// Redirect on first render — don't flash a screen behind it.
		if (typeof window !== 'undefined') {
			window.location.replace(signInUrl);
		}
		return (
			<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
				<div
					style={{
						position: 'fixed',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						background: '#0a0a0a',
						color: '#888',
						fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
					}}
				>
					Redirecting to sign in…
				</div>
			</ThemeProvider>
		);
	}

	// Enterprise mode: gate rendering on connection state.
	// NEVER show the DesktopShell with no WS connection — that's the "stale local UI" bug.
	if (isEnterprise) {
		// G5: fatal ConnectErrors get actionable CTA screens (Sign in / Reload / etc.)
		// via the desktop-shell router. The generic ConnectionErrorScreen is the
		// fallback for unknown or non-actionable kinds (network/server-closed).
		if (retryStatus === 'fatal' && lastError) {
			return (
				<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
					<FatalConnectScreen
						error={lastError}
						kernel={_enterpriseServerUrl}
						onBackToLauncher={handleBackToLauncher}
						renderFallback={() => (
							<ConnectionErrorScreen
								serverUrl={_enterpriseServerUrl}
								onBackToLauncher={handleBackToLauncher}
								onRetry={handleRetry}
							/>
						)}
					/>
				</ThemeProvider>
			);
		}
		if (retryStatus === 'retry_failed') {
			return (
				<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
					<ConnectionErrorScreen
						serverUrl={_enterpriseServerUrl}
						onBackToLauncher={handleBackToLauncher}
						onRetry={handleRetry}
					/>
				</ThemeProvider>
			);
		}

		if (retryStatus !== 'connected') {
			return (
				<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
					<ConnectingScreen />
				</ThemeProvider>
			);
		}
	}

	// Connected (enterprise) or local mode — render the full shell
	return (
		<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
			<TauriKhalAuthProvider>
				<DesktopShell />
			</TauriKhalAuthProvider>
		</ThemeProvider>
	);
}

// Static pack manifest. First-paint fallback only — the kernel registry is
// authoritative and lives in `useDesktopStore.installedAppRowsBySlug`.
const staticPacks = getRegisteredPacks();

/**
 * Resolve an appId to a known pack:
 *   1. Dynamic kernel registry (`os.apps.list` → `installedAppRowsBySlug`)
 *   2. Static `packs.json` fallback (first paint before NATS connects, offline)
 *
 * Returns `null` only when the appId is unknown in BOTH sources, which means
 * the launcher shouldn't render it as a tile in the first place.
 */
function resolvePack(appId: string): { id: string; name: string } | null {
	const row = useDesktopStore.getState().installedAppRowsBySlug[appId];
	if (row) {
		return { id: row.slug, name: row.store?.name ?? row.slug };
	}
	const fallback = staticPacks.find((p) => p.id === appId);
	if (fallback) return { id: fallback.id, name: fallback.name };
	return null;
}

// Memoize the per-appId Component wrapper so React sees a stable component
// type across WindowRenderer re-renders. Without this cache, every call to
// getComponent returns a fresh function, React treats each one as a new
// component, and DynamicAppBundle gets unmounted+remounted on every parent
// re-render — which puts pack windows in an infinite "Loading …" loop
// because the bundle load is cancelled before setComponent has a chance to
// commit.
const _dynamicAppCache: Record<
	string,
	ComponentType<{ windowId: string; meta?: Record<string, unknown> }>
> = {};

function getOrCreateDynamicAppComponent(
	appId: string,
): ComponentType<{ windowId: string; meta?: Record<string, unknown> }> {
	const cached = _dynamicAppCache[appId];
	if (cached) return cached;
	const Component = (props: { windowId: string; meta?: Record<string, unknown> }) => (
		<DynamicAppBundle {...props} meta={{ ...props.meta, appId }} />
	);
	Component.displayName = `DynamicApp(${appId})`;
	_dynamicAppCache[appId] = Component;
	return Component;
}

// Runtime v2 iframe apps render through KhalAppFrame instead of the v1
// DynamicAppBundle dynamic-import path. The wrapper is memoized per appId
// (keyed under `v2:` so it never collides with the v1 cache entry) so React
// sees a stable component type across WindowRenderer re-renders.
function getOrCreateKhalAppFrameComponent(
	appId: string,
): ComponentType<{ windowId: string; meta?: Record<string, unknown> }> {
	const cacheKey = `v2:${appId}`;
	const cached = _dynamicAppCache[cacheKey];
	if (cached) return cached;
	const Component = (props: { windowId: string; meta?: Record<string, unknown> }) => (
		<KhalAppFrame {...props} meta={{ ...props.meta, appId }} />
	);
	Component.displayName = `KhalAppFrame(${appId})`;
	_dynamicAppCache[cacheKey] = Component;
	return Component;
}

const appRenderer: AppComponentRenderer = {
	getComponent: (appId: string) => {
		// 1. Built-in components (marketplace, …) — never go through the kernel.
		const Builtin = BUILTIN_COMPONENTS[appId];
		if (Builtin) {
			Builtin.displayName = `Builtin(${appId})`;
			return Builtin;
		}

		// 2. Runtime v2 iframe apps — render through KhalAppFrame using the
		//    normalized `runtimeKind` from the installed-app row. extractRuntimeKind
		//    maps legacy `v2-iframe` and `image` → the canonical `iframe` kind, so
		//    clean and legacy registry rows both route here instead of silently
		//    falling back to the v1 bundle loader.
		//    launchUrl/origin reach the frame via win.meta (set by useLaunchApp).
		const row = useDesktopStore.getState().installedAppRowsBySlug[appId];
		if (row && extractRuntimeKind(row) === 'iframe') {
			return getOrCreateKhalAppFrameComponent(appId);
		}

		// 3. Dynamic packs from the kernel registry (with static packs.json as
		//    first-paint fallback). The bundle itself is fetched from the
		//    kernel at `${serverUrl}/apps/<id>/bundle.mjs` by DynamicAppBundle;
		//    first-party core packs may fall back to desktop-shipped artifacts.
		const pack = resolvePack(appId);
		if (!pack) return null;
		return getOrCreateDynamicAppComponent(appId);
	},
	getManifest: (appId: string) => {
		// Look up the installed-app row (populated by useDesktopStore.fetchApps
		// from the kernel's `os.apps.list` reply) so the gear panel + lifecycle
		// banner can read installedAppId / lifecycleState / declared env[]. Pre
		// core#336 kernels won't have these fields — they stay undefined and the
		// banner / Environment tab degrade silently.
		const row = useDesktopStore.getState().installedAppRowsBySlug[appId];
		const manifestJson = (row?.installedManifestJson ?? null) as
			| { env?: unknown }
			| null;
		const env = Array.isArray(manifestJson?.env)
			? (manifestJson.env as Array<Record<string, unknown>>).filter(
					(e) => typeof e?.key === 'string',
				) as unknown as import('@khal-os/desktop-shell').AppEnvManifestEntry[]
			: undefined;
		const installedAppId = row?.installedAppId;
		const lifecycleState = row?.lifecycleState;

		if (BUILTIN_COMPONENTS[appId]) {
			return {
				label: BUILTIN_LABELS[appId] ?? appId,
				showTabs: false,
				fullSizeContent: true,
				lifecycleState,
				env,
				installedAppId,
			};
		}
		const pack = resolvePack(appId);
		if (!pack) return undefined;
		return {
			label: pack.name,
			showTabs: false,
			fullSizeContent: true,
			lifecycleState,
			env,
			installedAppId,
		};
	},
};

setAppRenderer(appRenderer);
setAppEnvClient(appEnvClient);

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<Sentry.ErrorBoundary fallback={<p>An error occurred.</p>}>
			<App />
		</Sentry.ErrorBoundary>
	</React.StrictMode>
);
